import { SAAS_APP_HEADER_BAR_SHADOW_CLASS } from "@saas/shared/lib/saas-app-chrome-vars";
import type { CSSProperties } from "react";

/** `--marketing-site-header-height` on `:root`; synced by `MarketingSiteHeader`. */
export const MARKETING_SITE_HEADER_HEIGHT_VAR =
	"--marketing-site-header-height";

/** Sticky marketing header (banner + navbar) above demo sheets and overlays. */
export const MARKETING_SITE_HEADER_STACK_Z_CLASS = "z-[235]";

/**
 * Portaled {@link HeaderNavMenu} dropdown — above {@link MARKETING_SITE_HEADER_STACK_Z_CLASS}
 * and completion shell (`z-[215]`); default `HeaderNavMenu` uses `z-[230]` which sits under the header.
 */
export const MARKETING_NAV_DROPDOWN_CONTENT_Z_CLASS = "z-[241]";

/**
 * Pricing marketing overlays (schedule chat, checkout gate) — above sticky header stack.
 * Same layer as landing-video chrome fullscreen dialogs.
 */
export const MARKETING_PRICING_OVERLAY_STACK_Z_CLASS = "z-[250]";

/**
 * Condensed sticky summary under locked overlay nav, above scroll content.
 * Must stay below {@link MARKETING_PRICING_OVERLAY_LOCKED_NAV_Z_CLASS}.
 */
export const MARKETING_PRICING_OVERLAY_STICKY_SUMMARY_Z_CLASS = "z-[255]";

/**
 * Pinned checkout Pay / Confirm schedule bar — above form fields, below locked nav.
 */
export const MARKETING_PRICING_OVERLAY_STICKY_CTA_Z_CLASS = "z-[256]";

/**
 * Locked overlay NavBar (logo + close) above overlay body within the dialog stack.
 */
export const MARKETING_PRICING_OVERLAY_LOCKED_NAV_Z_CLASS = "z-[260]";

/**
 * Dropdown menus opened from within the pricing overlay (account / org pickers).
 * Must clear locked nav + sticky summary so the portal paints above the dialog.
 */
export const MARKETING_PRICING_OVERLAY_DROPDOWN_CONTENT_Z_CLASS = "z-[270]";

/**
 * Confirm / alert dialogs opened from the pricing overlay (e.g. remove booster).
 * Above overlay stack + overlay dropdowns (defaults AlertDialog is only z-[200]).
 */
export const MARKETING_PRICING_OVERLAY_ALERT_DIALOG_Z_CLASS = "z-[280]";

/** `--marketing-pricing-overlay-locked-nav-height` synced by PricingMarketingOverlayShell. */
export const MARKETING_PRICING_OVERLAY_LOCKED_NAV_HEIGHT_VAR =
	"--marketing-pricing-overlay-locked-nav-height";

/** Drop shadow under the full sticky header onto page content. */
export const MARKETING_SITE_HEADER_SHADOW_CLASS =
	SAAS_APP_HEADER_BAR_SHADOW_CLASS;

/** Problems band, landing CTA, and waitlist hero (left column): Tailwind gray-900 → near-black. */
export const MARKETING_LANDING_DARK_GRADIENT_BG_CLASS =
	"bg-gradient-to-b from-[#141924] to-[#111111]";

/** Tokens live in `apps/web/app/globals.css` `@layer theme` (`:root` / `.dark`). */

/** Panels / cards (#111111 in dark). */
export const MARKETING_SURFACE_PANEL_BG_CLASS =
	"bg-[color:var(--marketing-surface)]";

/**
 * GlassPanel tone on marketing hero / hex backdrops (waitlist confirm, newsletter confirm, etc.).
 * Matches org homepage {@link GlassPanel} default: `system` follows `html.dark` via surface tokens.
 */
export const MARKETING_HEX_BACKDROP_GLASS_PANEL_TONE = "system" as const;

/**
 * Repeated `hextile.png` wash. Dark mode: invert texture + lower opacity than legacy 0.12
 * so the pattern stays visible but quieter.
 */
export const MARKETING_HEXTILE_PATTERN_LAYER_CLASS =
	"opacity-8 dark:invert dark:opacity-[0.06]";

/** Full-bleed section background photos (features, FAQs, solution). */
export const MARKETING_FULL_BLEED_BACKGROUND_IMAGE_SIZES = "100vw";

/** lg-only hero corner screenshots (hidden below `lg`). */
export const MARKETING_HERO_SCREENSHOT_IMAGE_SIZES =
	"(max-width: 1023px) 0px, (max-width: 1279px) 196px, 300px";

/** Floated problem-card illustrations (192px display). */
export const MARKETING_PROBLEMS_CARD_IMAGE_SIZES = "192px";

/** Testimonial author avatars (56px display). */
export const MARKETING_TESTIMONIAL_AVATAR_IMAGE_SIZES = "56px";

/** Frosted white shells → solid `--marketing-surface` in dark (hero headline / subcopy blocks). */
export const MARKETING_LANDING_GLASS_PANEL_CLASS =
	"bg-white/50 dark:bg-[color:var(--marketing-surface)]";

/**
 * Hero glass inner blocks: solid white in light (not off-white) → `--marketing-surface` in dark.
 */
export const MARKETING_LANDING_HERO_GLASS_INNER_CLASS =
	"bg-white dark:bg-[color:var(--marketing-surface)]";

/** Launch countdown badge shell (light translucent → dark surface). */
export const MARKETING_LANDING_COUNTDOWN_BADGE_SHELL_CLASS =
	"border-white/80 bg-white dark:border-white/10 dark:bg-[color:var(--marketing-surface)]";

/** CTA rail availability badge (frosted light → dark surface). */
export const MARKETING_AVAILABILITY_BADGE_SHELL_CLASS =
	"bg-white/90 dark:bg-[color:var(--marketing-surface)]";

/** Slate demo panels / mission band (light `slate-50` → dark `--marketing-surface`). */
export const MARKETING_LANDING_SLATE_PANEL_BG_CLASS =
	"bg-slate-50 dark:bg-[color:var(--marketing-surface)]";

/** Elevated cards (#141924 in dark). */
export const MARKETING_SURFACE_ELEVATED_BG_CLASS =
	"bg-[color:var(--marketing-surface-elevated)]";

/** Banner strip wash behind coloured blurs (white / #141924 @ 70%). */
export const MARKETING_BANNER_UNDERLAY_BG_CLASS =
	"bg-[color:var(--marketing-banner-underlay)]";

/** Opaque seam between marketing banner and navbar rows. */
export const MARKETING_BANNER_BOTTOM_BORDER_CLASS = "border-b border-border";

/** Banner row stacks above the marketing navbar (`MARKETING_NAVBAR_BELOW_BANNER_Z_CLASS`). */
export const MARKETING_BANNER_ABOVE_NAVBAR_Z_CLASS = "relative z-[50]";

/** Soft drop shadow under the banner onto the navbar row. */
export const MARKETING_BANNER_BOTTOM_SHADOW_CLASS =
	"shadow-[0_2px_6px_-1px_rgb(0_0_0/0.07)] dark:shadow-[0_2px_6px_-1px_rgb(0_0_0/0.25)]";

/** Marketing nav row shell background. */
export const MARKETING_NAV_SHELL_BG_CLASS =
	"bg-[color:var(--marketing-nav-shell-bg)]";

/** Navbar row under the marketing banner strip. */
export const MARKETING_NAVBAR_BELOW_BANNER_Z_CLASS = "relative z-40";

/**
 * Translucent #141924 overlay (opaque in light via transparent token).
 * Matches completion-flow dark wash intent.
 */
export const MARKETING_SECTION_OVERLAY_LAYER_CLASS =
	"pointer-events-none absolute inset-0 z-[1] bg-[color:var(--marketing-section-overlay)]";

/** Stronger overlay for FAQs + footer. */
export const MARKETING_SECTION_OVERLAY_STRONG_LAYER_CLASS =
	"pointer-events-none absolute inset-0 z-[1] bg-[color:var(--marketing-section-overlay-strong)]";

/** Hero / mission-style section fill (gradient light, flat dark). */
export function marketingHeroFillStyle(minHeight: string): CSSProperties {
	return {
		background: "var(--marketing-hero-fill)",
		minHeight,
	};
}

/** Pass to {@link LogoLoop} `fadeOutColor` so fades track `--marketing-logoloop-fade`. */
export const MARKETING_LOGOLOOP_FADE_COLOR = "var(--marketing-logoloop-fade)";
